import { CodeNameModel } from 'components/common/types';
import { DIRECT_SALES_SWEEP } from 'components/common/types/DirectSalesCampaign.types';
import { SelectionPermission } from 'store/productManagement/reducer.types';
import { getNoRestrictionsDisplayValue } from '../../utils/getNoRestrictionsDisplayValue';

type ChipKeys = 'Default' | 'Minimum' | 'Maximum' | 'Sweep type';

type SelectedValue = Partial<
  Record<
    ChipKeys,
    {
      included?: CodeNameModel[];
      excluded?: CodeNameModel[];
    }
  >
>;

interface BuildSelectedValueParams {
  sot?: {
    value?: {
      defaultValue?: number;
      minValue?: number;
      maxValue?: number;
    };
    selectionPermission?: SelectionPermission;
  };
  sweep?: {
    value?: keyof typeof DIRECT_SALES_SWEEP;
    selectionPermission?: SelectionPermission;
  };
}

const buildChip = (label: string, value?: string): SelectedValue =>
  value
    ? {
        [label]: {
          included: [{ code: '', name: value }],
        },
      }
    : {};

const buildSotChips = (sot?: BuildSelectedValueParams['sot']): SelectedValue => {
  if (!sot) return {};

  const { value, selectionPermission } = sot;

  const isNoRestrictions =
    selectionPermission === SelectionPermission.DISABLED || selectionPermission === SelectionPermission.ALL_OPTIONS;

  const isRangeValues = selectionPermission === SelectionPermission.RANGE_VALUE;
  const isFixedValue = selectionPermission === SelectionPermission.FIXED_VALUE;

  return {
    ...buildChip(
      'Default',
      value?.defaultValue !== undefined && (isFixedValue || isRangeValues) ? `${value.defaultValue}%` : undefined,
    ),
    ...buildChip('Minimum', value?.minValue !== undefined && isRangeValues ? `${value.minValue}%` : undefined),
    ...buildChip('Maximum', value?.maxValue !== undefined && isRangeValues ? `${value.maxValue}%` : undefined),
    ...buildChip('SOT', isNoRestrictions ? getNoRestrictionsDisplayValue(selectionPermission)[0] : undefined),
  };
};

const buildSweepChips = (sweep?: BuildSelectedValueParams['sweep']): SelectedValue => {
  if (!sweep) return {};

  const isNoRestrictions =
    sweep.selectionPermission === SelectionPermission.DISABLED ||
    sweep.selectionPermission === SelectionPermission.ALL_OPTIONS;

  const isFixedValue = sweep.selectionPermission === SelectionPermission.FIXED_VALUE;

  return {
    ...buildChip('Sweep type', isFixedValue && sweep.value ? DIRECT_SALES_SWEEP[sweep.value].label : undefined),
    ...buildChip('Sweep', isNoRestrictions ? getNoRestrictionsDisplayValue(sweep.selectionPermission)[0] : undefined),
  };
};

export const buildSelectedValue = ({ sot, sweep }: BuildSelectedValueParams): SelectedValue => ({
  ...buildSotChips(sot),
  ...buildSweepChips(sweep),
});

interface IsConfirmDisabledParams {
  sotSelectedPermission: CodeNameModel;
  sweepSelectedPermission: CodeNameModel;
  defaultSotValue?: number;
  minSotValue?: number;
  maxSotValue?: number;
  sweepValue?: unknown;
}

export const isConfirmDisabled = ({
  sotSelectedPermission,
  sweepSelectedPermission,
  defaultSotValue,
  minSotValue,
  maxSotValue,
  sweepValue,
}: IsConfirmDisabledParams): boolean => {
  const isSotCorrect =
    sotSelectedPermission.code === SelectionPermission.ALL_OPTIONS ||
    (sotSelectedPermission.code === SelectionPermission.FIXED_VALUE && defaultSotValue !== undefined) ||
    (sotSelectedPermission.code === SelectionPermission.RANGE_VALUE &&
      (minSotValue !== undefined || maxSotValue !== undefined)) ||
    sotSelectedPermission.code === SelectionPermission.DISABLED;

  const isSweepCorrect =
    sweepSelectedPermission.code === SelectionPermission.ALL_OPTIONS ||
    (sweepSelectedPermission.code === SelectionPermission.FIXED_VALUE && !!sweepValue) ||
    sweepSelectedPermission.code === SelectionPermission.DISABLED;

  return !isSotCorrect || !isSweepCorrect;
};

export const clampValue = (value: number | undefined, min: number, max: number): number =>
  Math.min(Math.max(value ?? min, min), max);

export const getCorrectedSot = (
  value: number | undefined,
  min: number | undefined,
  max: number | undefined,
  isFixed: boolean,
): number | undefined => {
  if (isFixed) return value;
  const minVal = min ?? 0;
  const maxVal = max ?? 100;
  return clampValue(value, minVal, maxVal);
};
